import type { ReactElement } from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame } from 'remotion';

// Constants
import {
  TEXT_ANIMATION,
  CURVED_LINES_PATH,
  TEXT_CONTENT,
  SIDE_LINE_DURATION,
  LAST_LINE_EXTRA_DELAY,
} from './constants';
import { CURVED_LINES_DEFAULT_STYLE } from '../Scene5a/constants';
import { CLAMP_BOTH } from '@/constants/animations';

// Components
import { CurvedLinesBackground } from '../Scene5a/components/CurvedLinesBackground';
import { ConfettiScene8 } from './components/ConfettiScene8';
import { HighlightedTextLine } from './components/HighlightedText';

// Utils
import {
  calculateLineDelays,
  calculateSideLineStart,
  parseHighlightedText,
} from './utils';
import { employeeYearInReviewTheme } from '../../theme';

const TEXT_LINES = TEXT_CONTENT.split('\n').map((line) => ({
  segments: parseHighlightedText(line),
}));

interface Scene8Props {
  animationDelay?: number;
}

export const Scene8 = ({ animationDelay = 0 }: Scene8Props): ReactElement => {
  const frame = useCurrentFrame();

  const lineDelays = calculateLineDelays({
    textLines: TEXT_LINES,
    animationDelay,
    textAnimation: TEXT_ANIMATION,
    lastLineExtraDelay: LAST_LINE_EXTRA_DELAY,
  });

  // Side lines animate in after the last text line has fully faded in
  const sideLineStart = calculateSideLineStart({
    textLines: TEXT_LINES,
    lineDelays,
    framesPerWord: TEXT_ANIMATION.FRAMES_PER_WORD,
    slowWordFadeDuration: TEXT_ANIMATION.WORD_FADE_DURATION_SLOW,
  });
  const sideLineProgress = Easing.out(Easing.cubic)(
    interpolate(
      frame,
      [sideLineStart, sideLineStart + SIDE_LINE_DURATION],
      [0, 1],
      CLAMP_BOTH,
    ),
  );

  const { colors } = employeeYearInReviewTheme;

  return (
    <AbsoluteFill className="overflow-hidden bg-main-gradient">
      {/* Curved Lines Background (tilted clockwise, slower animation) */}
      <div className="absolute inset-0 rotate-[15deg] scale-[1.15]">
        <CurvedLinesBackground
          path={CURVED_LINES_PATH}
          lineStyle={{ ...CURVED_LINES_DEFAULT_STYLE, opacity: 0.15 }}
          animation={{ duration: 120, delay: animationDelay }}
        />
      </div>

      {/* Confetti / Fireworks effect */}
      <ConfettiScene8 startFrame={animationDelay} />

      {/* Text Content */}
      <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 -rotate-[5deg] flex-col justify-center gap-0.5 w-[1170px]">
        {/* Left decorative line */}
        <div
          className={`absolute bottom-0 right-full mr-6 h-2 w-full origin-right rounded-full ${colors.bgTertiary}`}
          style={{
            transform: `scaleX(${sideLineProgress})`,
          }}
        />

        {/* Right decorative line */}
        <div
          className={`absolute left-full top-24 ml-12 h-2 w-full origin-left rounded-full ${colors.bgTertiary}`}
          style={{
            transform: `scaleX(${sideLineProgress})`,
          }}
        />

        {TEXT_LINES.map((line, index) => {
          const delay =
            lineDelays[index] ??
            TEXT_ANIMATION.FIRST_LINE_DELAY + animationDelay;

          return (
            <HighlightedTextLine
              key={index}
              segments={line.segments}
              delay={delay}
              framesPerWord={TEXT_ANIMATION.FRAMES_PER_WORD}
              highlightDelay={TEXT_ANIMATION.HIGHLIGHT_DELAY}
              highlightDuration={TEXT_ANIMATION.HIGHLIGHT_DURATION}
              isLastLine={index === TEXT_LINES.length - 1}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
